
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { WishlistItemUser, Section } from "@/types/wishlist";
import { toast } from "sonner";
import { Gift, ExternalLink } from "lucide-react";
import { wishlistApi } from "@/api/wishlist";
import { WishlistItem } from "@/components/friends/WishlistItem";

const SharedSection = () => {
  const { userId, sectionId } = useParams<{ userId: string; sectionId: string }>();
  const [section, setSection] = useState<Section | null>(null);
  const [items, setItems] = useState<WishlistItemUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSectionData = async () => {
      if (!userId || !sectionId) {
        toast.error("Invalid section URL");
        return;
      }

      try {
        setLoading(true);
        const [sectionData, sectionItems] = await Promise.all([
          wishlistApi.getPublicSection(Number(userId), Number(sectionId)),
          wishlistApi.getPublicSectionItems(Number(userId), Number(sectionId))
        ]);

        setSection(sectionData);
        setItems(sectionItems);
      } catch (error: any) {
        toast.error(error?.message || "Failed to load section");
      } finally {
        setLoading(false);
      }
    };

    fetchSectionData();
  }, [userId, sectionId]);

  return (
    <Layout hideNavbar hideSidebar>
      <div className="min-h-screen bg-gradient-to-br from-[#F1F0FB] to-[#E5DEFF] py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-6 mb-8 border-none shadow-lg bg-white/90 dark:bg-card backdrop-blur-sm">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin h-8 w-8 border-t-2 border-[#9b87f5] border-r-2 rounded-full" />
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#9b87f5] to-[#D946EF] flex items-center justify-center mb-4">
                    <Gift className="h-8 w-8 text-white" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#9b87f5] to-[#7E69AB] bg-clip-text text-transparent mb-2">
                    {section?.name || "Section"}
                  </h1>
                  {section?.description && section.description.trim() !== "" && (
                    <p className="text-gray-600 dark:text-muted-foreground max-w-md">
                      {section.description}
                    </p>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  {items.map((item) => (
                    <WishlistItem
                      key={item.id}
                      item={item}
                    />
                  ))}
                  {items.length === 0 && (
                    <div className="col-span-2 text-center py-12">
                      <p className="text-gray-500 dark:text-muted-foreground">This section is empty</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SharedSection;
